import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useI18n } from '../lib/i18n/I18nProvider';
import { useSession } from '../lib/auth/session';
import { ApiError, stripBidiControls } from '../lib/api/problem';
import { ConfirmationRequired, newIntent, useApiAction } from '../lib/api/queries';
import {
  decodeGrant,
  decodeRequest,
  encodeGrant,
  encodeRequest,
  grantMatches,
  intentDigest,
  type ExceptionProposal,
  type HandoffGrant,
  type HandoffRequest,
} from '../lib/auth/handoff';
import { Field, TextArea } from './primitives';
import { ErrorState } from './states';

/**
 * The guard patterns of design/p13-dashboards.md §7, implemented once and used everywhere.
 */

/**
 * THE RESOLUTION AN EXCEPTION APPROVAL IS ABOUT, in one line (BE-3).
 *
 * Rendered identically on BOTH screens, because the whole point of the hand-off is that the two
 * principals are looking at the same act: the outcome first, since it is the half that decides what
 * the approval authorises, then the ownership and credit move a correction carries. Every field here
 * is one `crm.ExceptionResolutionDigest` binds — the deal ids sorted, as the server sorts them — so
 * an approver reading this line has read what their approval will be spent on.
 */
function exceptionSummary(e: ExceptionProposal): string {
  return [
    e.decision,
    ...(e.new_brokerage_org_id ? [`brokerage ${e.new_brokerage_org_id}`] : []),
    ...(e.new_broker_user_id ? [`broker ${e.new_broker_user_id}`] : []),
    ...(e.expo_id ? [`expo ${e.expo_id}`] : []),
    ...(e.deal_ids && e.deal_ids.length > 0 ? [`deals ${[...e.deal_ids].sort().join(', ')}`] : []),
  ].join(' · ');
}

/**
 * Gives the operator a clear consequence first. Implementation metadata remains available to
 * support staff through progressive disclosure instead of competing with the business action.
 */
export function AuditStatement({
  summary,
  children,
}: {
  summary?: ReactNode;
  children?: ReactNode;
}): ReactElement {
  const { t } = useI18n();
  return (
    <div className="guard">
      <span className="guard__label">{t('guard.audit')}</span>
      <div>{summary ?? <p>{t('guard.audit.body')}</p>}</div>
      {children ? (
        <details className="technical-details">
          <summary>{t('details.technical')}</summary>
          <div className="technical-details__body">{children}</div>
        </details>
      ) : null}
    </div>
  );
}

/**
 * Step-up (`SHR-10`), FOR ONE ACTION.
 *
 * `POST /v1/auth/step-up` takes the action, the backend evaluates `RequireStepUp(az, action, now)`
 * with the action THAT ROUTE performs, and `iam.StepUp` resets the assurance as soon as a grant is
 * consumed. There is therefore no such thing as "being elevated": there is a grant for
 * `expo.release.activate`, and it is worth nothing to `content.moderate`.
 *
 * This panel is consequently never a global control. It appears beside one operation, names that
 * operation's permission code, and the grant it records can only be presented for that code.
 */
export function StepUpPanel({
  action,
  onElevated,
}: {
  action: string;
  onElevated?: () => void;
}): ReactElement {
  const { t } = useI18n();
  const session = useSession();
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await session.stepUp(code, action);
      setCode('');
      onElevated?.();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [session, code, action, onElevated]);

  const grant = session.grantFor(action);

  return (
    <div className="guard" data-testid="step-up-panel" data-action={action}>
      <span className="guard__label">{t('stepUp.title')}</span>
      <p>{t('stepUp.body')}</p>
      {grant ? (
        <p className="muted" data-testid="step-up-grant">
          {t('stepUp.granted', { time: grant.expiresAt })}
        </p>
      ) : null}
      <Field
        label={t('auth.totp')}
        name={`step-up-totp-${action}`}
        value={code}
        onChange={setCode}
        ltr
        autoComplete="one-time-code"
      />
      {error ? <ErrorState error={error} /> : null}
      <div className="row">
        <button type="button" className="btn" disabled={busy || code.length === 0} onClick={() => void submit()}>
          {t('stepUp.confirm')}
        </button>
        {grant ? (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => session.releaseGrant(action)}
          >
            {t('stepUp.release')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export interface ApprovalRecord {
  approval_id: string;
  action?: string;
  aggregate_type?: string;
  aggregate_id?: string;
  expected_version?: number;
  approver_user_id?: string;
  initiator_user_id?: string;
  status?: string;
  expires_at?: string;
}

/**
 * The INITIATOR's half of four eyes (`SHR-11`).
 *
 * It collects nothing that belongs to the approver — no credentials, no second factor, and above
 * all no `approver_user_id` typed by the person the rule exists to constrain. It states the act,
 * produces the hand-off request the second principal reads in their own session, and accepts the
 * approval that principal produced.
 *
 * The approver id the operation ends up sending comes OUT of that approval, which only an
 * authenticated approver session could have created.
 */
export function ApprovalHandoff({
  request,
  onGranted,
}: {
  request: HandoffRequest | null;
  onGranted: (grant: HandoffGrant | null) => void;
}): ReactElement {
  const { t } = useI18n();
  const [token, setToken] = useState('');
  const [accepted, setAccepted] = useState<HandoffGrant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const requestToken = useMemo(() => {
    if (!request) return '';
    try {
      return encodeRequest(request);
    } catch {
      return '';
    }
  }, [request]);

  /**
   * VALIDITY IS DERIVED FROM THE ACT IN HAND, ON EVERY RENDER (round-3 #12).
   *
   * `grantMatches` was right and was asked once — at the moment the token was ENTERED. Everything
   * the initiator could still change afterwards (the release, the takedown subject, the version the
   * screen re-read) moved out from under an answer nobody re-computed, so the accepted badge went
   * on claiming an approval for an act that approval was never given for. The only thing that would
   * have contradicted it was the server's refusal, AFTER the operator pressed the button.
   *
   * So the accepted token is remembered and its VALIDITY is not: it is recomputed against the
   * current request every time, and the moment the two disagree the screen says the intent changed
   * and the parent is handed `null` instead of a grant it cannot spend.
   */
  const grant = accepted !== null && request !== null && grantMatches(accepted, request) ? accepted : null;
  const stale = accepted !== null && grant === null;

  /**
   * The parent is told what the CURRENT act is approved for, not what was pasted.
   *
   * The callback is read through a ref so an inline arrow at the call site cannot turn this into a
   * per-render notification: it fires when the derived grant changes, and `grant` is either the
   * stored object or `null`, so its identity is stable while nothing has moved.
   */
  const notify = useRef(onGranted);
  useEffect(() => {
    notify.current = onGranted;
  });
  useEffect(() => {
    notify.current(grant);
  }, [grant]);

  const accept = useCallback(
    (value: string) => {
      setToken(value);
      setError(null);
      setAccepted(null);
      if (value.trim() === '') return;
      let decoded: HandoffGrant;
      try {
        decoded = decodeGrant(value);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return;
      }
      if (!request || !grantMatches(decoded, request)) {
        // A grant raised for a different act, or for a version that has since moved on, is refused
        // here rather than sent: it is the same defect as spending a step-up on another mutation.
        setError(t('approval.mismatch'));
        return;
      }
      setAccepted(decoded);
    },
    [request, t],
  );

  return (
    <div className="guard" data-testid="approval-handoff">
      <span className="guard__label">{t('approval.title')}</span>
      <p>{t('approval.body')}</p>
      <p className="muted">{t('approval.handoff.hint')}</p>
      {request ? (
        <>
          <details className="technical-details">
            <summary>{t('details.technical')}</summary>
            <dl className="kv technical-details__body">
            <dt>{t('approval.action')}</dt>
            <dd className="ltr">{request.action}</dd>
            {request.aggregateId ? (
              <>
                <dt>{t('approval.aggregate')}</dt>
                <dd className="ltr">
                  {request.aggregateType} · {request.aggregateId}
                </dd>
                <dt>{t('approval.expectedVersion')}</dt>
                <dd className="ltr numeral">{request.expectedVersion}</dd>
              </>
            ) : null}
            {request.survivorPersonId ? (
              <>
                <dt>survivor_person_id</dt>
                <dd className="ltr">{request.survivorPersonId}</dd>
                <dt>absorbed_person_id</dt>
                <dd className="ltr">{request.absorbedPersonId}</dd>
              </>
            ) : null}
            {request.proposal ? (
              <>
                <dt>{t('approval.proposal')}</dt>
                <dd className="ltr">{request.proposal.role_codes.join(', ')}</dd>
              </>
            ) : null}
            {request.takedown ? (
              <>
                <dt>{t('approval.takedown')}</dt>
                <dd className="ltr">
                  {request.takedown.subject_type} · {request.takedown.subject_id} ·{' '}
                  {request.takedown.reason_code}
                </dd>
              </>
            ) : null}
            {request.release ? (
              <>
                <dt>{t('approval.release')}</dt>
                <dd className="ltr">{request.release.release_id}</dd>
              </>
            ) : null}
            {request.exception ? (
              <>
                <dt>{t('approval.exception')}</dt>
                <dd className="ltr" data-testid="handoff-exception">
                  {exceptionSummary(request.exception)}
                </dd>
              </>
            ) : null}
            {/* The same string the approver's desk displays. Two operators comparing it are
                comparing the whole act, not four fields of it. */}
            <dt>{t('approval.intent')}</dt>
            <dd className="ltr" data-testid="handoff-intent-digest">
              {intentDigest(request)}
            </dd>
            </dl>
          </details>
          <TextArea
            label={t('approval.request.label')}
            name="approval-request-token"
            value={requestToken}
            onChange={() => undefined}
            readOnly
            hint={t('approval.request.hint')}
          />
          <div className="row">
            <button
              type="button"
              className="btn btn--secondary btn--small"
              onClick={() => {
                void navigator.clipboard?.writeText(requestToken).then(
                  () => setCopied(true),
                  () => setCopied(false),
                );
              }}
            >
              {copied ? t('state.copied') : t('approval.request.copy')}
            </button>
          </div>
        </>
      ) : (
        <p className="muted">{t('approval.request.incomplete')}</p>
      )}

      <TextArea
        label={t('approval.grant.label')}
        name="approval-grant-token"
        value={token}
        onChange={accept}
        hint={t('approval.grant.hint')}
        {...(error ? { error } : {})}
      />
      {grant ? (
        <p className="badge badge--ok" data-testid="approval-accepted">
          <span className="ltr">
            {grant.approvalId}
            {grant.approverUserId ? ` · ${t('approval.approver')} ${grant.approverUserId}` : ''}
          </span>
        </p>
      ) : stale ? (
        // The token is still in the box, and it is still a real approval — of a DIFFERENT act. The
        // screen says which of those two facts matters here.
        <p className="badge badge--warn" data-testid="approval-stale" role="status">
          {t('approval.stale')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The APPROVER's half, which only ever runs in the approver's own session.
 *
 * `POST /v1/approvals`'s handler says it plainly: "THE CALLER IS THE APPROVER, AND IT IS HELD TO
 * EVERY CLAUSE THE ACT ITSELF IS HELD TO" — it must hold the action's permission and be inside its
 * own step-up window. So the desk shows the act, requires the step-up for that exact action, names
 * the initiator, and posts under whatever session the browser it is running in holds.
 */
export function ApproverDesk(): ReactElement {
  const { t } = useI18n();
  const session = useSession();
  const [token, setToken] = useState('');
  const [request, setRequest] = useState<HandoffRequest | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [initiator, setInitiator] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [recorded, setRecorded] = useState<ApprovalRecord | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * BOTH WRITES GO THROUGH AN INTENT (round-2 H5).
   *
   * They used to call `apiRequest` directly, which mints a fresh `Idempotency-Key` per attempt. The
   * failure that matters is the LOST 201: the approval committed, the approver's step-up was spent
   * on it, and the desk held neither the approval id nor the key that would replay it — so the only
   * thing the operator could do was press the button again, generate a second key, and meet their
   * own pending approval. `useApiAction` freezes the intent, `retryLast` re-sends that exact key,
   * and the server replays the stored 201 with the approval id in it.
   */
  const create = useApiAction<ApprovalRecord>([]);
  const revokeAction = useApiAction([]);
  const busy = create.isPending || revokeAction.isPending;

  const read = useCallback((value: string) => {
    setToken(value);
    setRecorded(null);
    setDecodeError(null);
    setRequest(null);
    if (value.trim() === '') return;
    try {
      const req = decodeRequest(value);
      setRequest(req);
      setReason(req.reasonCode);
      if (req.note) setNote(req.note);
    } catch (e) {
      setDecodeError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const grant = request ? session.grantFor(request.action) : null;

  /**
   * THE DIGEST OF THE ACT THE APPROVER IS LOOKING AT.
   *
   * It is derived from `request`, which is set once by `read` and never edited by this screen —
   * `reason` and `note` are the approver's own annotations and are deliberately outside it, because
   * the server does not bind an approval by them either.
   */
  const digest = useMemo(() => (request ? intentDigest(request) : ''), [request]);

  const submit = useCallback(() => {
    if (!request) return;
    const kind = request.kind ?? 'aggregate';
    if (kind === 'identity') {
      create.mutate(
        newIntent({
          path: '/v1/identity-approvals',
          action: request.action,
          label: `approve ${request.action}`,
          body: {
            action: request.action,
            survivor_person_id: request.survivorPersonId,
            absorbed_person_id: request.absorbedPersonId,
            initiator_user_id: initiator.trim(),
          },
        }),
        {
          onSuccess: (res) => {
            const raw = res as unknown as { id?: string; approval_id?: string; expires_at?: string };
            setRecorded({
              approval_id: raw.approval_id ?? raw.id ?? '',
              action: request.action,
              ...(raw.expires_at ? { expires_at: raw.expires_at } : {}),
            });
          },
        },
      );
      return;
    }
    const body: Record<string, unknown> = {
      action: request.action,
      aggregate_type: request.aggregateType,
      aggregate_id: request.aggregateId,
      expected_version: request.expectedVersion,
      initiator_user_id: initiator.trim(),
      reason_code: reason,
      note,
    };
    // THE PROPOSAL UNION, POSTED AS THE SERVER SPELLS IT. `handleCreateApproval` counts the members
    // and refuses two, and requires the one its aggregate carries; `decodeRequest` has already held
    // this token to the same rule, so at most one of these is present.
    if (request.proposal) body['proposal'] = request.proposal;
    if (request.takedown) body['takedown'] = request.takedown;
    if (request.release) body['release'] = request.release;
    // THE EXCEPTION RESOLUTION (BE-3), sent as the server spells it. The optional members are
    // omitted when empty rather than sent as `""`: `optionalUUID` reads an absent field as "none
    // named", which is what `crm.ExceptionResolutionDigest` renders as `-`.
    if (request.exception) {
      const e = request.exception;
      body['exception'] = {
        decision: e.decision,
        ...(e.new_brokerage_org_id ? { new_brokerage_org_id: e.new_brokerage_org_id } : {}),
        ...(e.new_broker_user_id ? { new_broker_user_id: e.new_broker_user_id } : {}),
        ...(e.expo_id ? { expo_id: e.expo_id } : {}),
        ...(e.deal_ids && e.deal_ids.length > 0 ? { deal_ids: e.deal_ids } : {}),
      };
    }
    create.mutate(
      newIntent({ path: '/v1/approvals', action: request.action, label: `approve ${digest}`, body }),
      { onSuccess: setRecorded },
    );
  }, [request, initiator, reason, note, create, digest]);

  const revoke = useCallback(() => {
    if (!recorded || !request) return;
    revokeAction.mutate(
      newIntent({
        path: `/v1/approvals/${recorded.approval_id}`,
        method: 'DELETE',
        label: `withdraw ${recorded.approval_id}`,
      }),
      { onSuccess: () => setRecorded(null) },
    );
  }, [recorded, request, revokeAction]);

  const grantToken =
    recorded && request
      ? encodeGrant({
          kind: request.kind ?? 'aggregate',
          approvalId: recorded.approval_id,
          action: request.action,
          // Display text for the initiator's screen — the operation itself never sends it.
          ...(recorded.approver_user_id ? { approverUserId: recorded.approver_user_id } : {}),
          ...(request.aggregateType ? { aggregateType: request.aggregateType } : {}),
          ...(request.aggregateId ? { aggregateId: request.aggregateId } : {}),
          ...(request.expectedVersion !== undefined ? { expectedVersion: request.expectedVersion } : {}),
          ...(request.survivorPersonId ? { survivorPersonId: request.survivorPersonId } : {}),
          ...(request.absorbedPersonId ? { absorbedPersonId: request.absorbedPersonId } : {}),
          intent: digest,
          ...(recorded.expires_at ? { expiresAt: recorded.expires_at } : {}),
        })
      : '';

  const error = create.error ?? revokeAction.error ?? null;

  return (
    <div className="guard" data-testid="approver-desk">
      <span className="guard__label">{t('approval.desk.title')}</span>
      <p>{t('approval.desk.body')}</p>

      <TextArea
        label={t('approval.request.label')}
        name="desk-request-token"
        value={token}
        onChange={read}
        hint={t('approval.desk.paste')}
        {...(decodeError ? { error: decodeError } : {})}
      />

      {request ? (
        <>
          <details className="technical-details">
            <summary>{t('details.technical')}</summary>
            <dl className="kv technical-details__body">
            <dt>{t('approval.action')}</dt>
            <dd className="ltr">{request.action}</dd>
            {request.aggregateId ? (
              <>
                <dt>{t('approval.aggregate')}</dt>
                <dd className="ltr">
                  {request.aggregateType} · {request.aggregateId}
                </dd>
                <dt>{t('approval.expectedVersion')}</dt>
                <dd className="ltr numeral">{request.expectedVersion}</dd>
              </>
            ) : null}
            {request.survivorPersonId ? (
              <>
                <dt>survivor_person_id</dt>
                <dd className="ltr">{request.survivorPersonId}</dd>
                <dt>absorbed_person_id</dt>
                <dd className="ltr">{request.absorbedPersonId}</dd>
              </>
            ) : null}
            {request.label ? (
              <>
                <dt>{t('approval.act')}</dt>
                <dd>{request.label}</dd>
              </>
            ) : null}
            {request.proposal ? (
              <>
                <dt>{t('approval.proposal')}</dt>
                <dd className="ltr">
                  {request.proposal.role_codes.join(', ')}
                  {request.proposal.participation_ids?.length
                    ? ` · participations: ${request.proposal.participation_ids.join(', ')}`
                    : ''}
                  {request.proposal.expo_ids?.length ? ` · expos: ${request.proposal.expo_ids.join(', ')}` : ''}
                </dd>
              </>
            ) : null}
            {/* THE PROPOSAL IS SHOWN, NOT JUST CARRIED. The server digests these fields and the act
                re-digests them when it spends the approval, so they are literally what is being
                approved — an approver who could not see them would be approving an expo id. */}
            {request.takedown ? (
              <>
                <dt>{t('approval.takedown')}</dt>
                <dd className="ltr">
                  {request.takedown.subject_type} · {request.takedown.subject_id} ·{' '}
                  {request.takedown.reason_code}
                </dd>
              </>
            ) : null}
            {request.release ? (
              <>
                <dt>{t('approval.release')}</dt>
                <dd className="ltr">{request.release.release_id}</dd>
              </>
            ) : null}
            {request.exception ? (
              <>
                <dt>{t('approval.exception')}</dt>
                <dd className="ltr" data-testid="desk-exception">
                  {exceptionSummary(request.exception)}
                </dd>
              </>
            ) : null}
            <dt>{t('approval.intent')}</dt>
            <dd className="ltr" data-testid="desk-intent-digest">
              {digest}
            </dd>
            </dl>
          </details>

          <Field
            label={t('approval.initiator')}
            name="desk-initiator"
            value={initiator}
            onChange={setInitiator}
            ltr
            required
            hint={t('approval.initiator.hint')}
          />
          <Field label={t('approval.reason')} name="desk-reason" value={reason} onChange={setReason} ltr required />
          <TextArea label={t('guard.note')} name="desk-note" value={note} onChange={setNote} />

          {/* The approver is held to the same step-up the act itself is held to. */}
          {grant ? null : <StepUpPanel action={request.action} />}

          {error ? (
            <RefusalWithRemedy
              error={error}
              action={request.action}
              retryExact={create.error ? create.retryLast : revokeAction.retryLast}
            />
          ) : null}

          {recorded ? (
            <>
              <TextArea
                label={t('approval.grant.label')}
                name="desk-grant-token"
                value={grantToken}
                onChange={() => undefined}
                readOnly
                hint={t('approval.grant.return')}
              />
              <div className="row">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={() => {
                    void navigator.clipboard?.writeText(grantToken).then(
                      () => setCopied(true),
                      () => setCopied(false),
                    );
                  }}
                >
                  {copied ? t('state.copied') : t('approval.grant.copy')}
                </button>
                <button type="button" className="btn btn--destructive" disabled={busy} onClick={revoke}>
                  {t('approval.revoke')}
                </button>
              </div>
            </>
          ) : (
            <div className="row">
              <button
                type="button"
                className="btn"
                disabled={busy || !grant || initiator.trim() === ''}
                onClick={submit}
              >
                {t('approval.create')}
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

/**
 * The remedy for a refusal — NEVER a different act.
 *
 * Three rules, each of which the previous version broke somewhere:
 *
 *   - a step-up refusal offers the step-up FOR THE ACTION THAT WAS REFUSED, and then re-sends the
 *     exact same request (same idempotency key, same `If-Match`);
 *   - a version conflict is not retryable at all: the version in hand is stale, so the screen
 *     re-reads and the operator confirms the act again, which mints a new intent;
 *   - anything else the server DECIDED is rendered and NOT offered a retry button, because
 *     repeating a decided refusal only repeats it. An outcome the client cannot name — a transport
 *     failure or a 5xx, see `ApiError.outcomeUnknown` — offers to resend instead, and resending is
 *     safe precisely because the key is the intent's: the server replays what it stored rather than
 *     performing the act twice.
 */
export function RefusalWithRemedy({
  error,
  action,
  retryExact,
  onReread,
  conflictNote,
}: {
  error: unknown;
  /** The permission code the refused request named. */
  action: string;
  /** Re-sends the exact failed intent. */
  retryExact: () => void;
  /**
   * Re-reads the aggregate after a version conflict.
   *
   * PASS IT ONLY IF IT REALLY RE-READS. A handler that merely clears the screen renders a button
   * labelled "re-read and start again" over an operation that read nothing — see `conflictNote`.
   */
  onReread?: () => void;
  /**
   * What to tell the operator instead of the standard "re-read the record and confirm again", for a
   * surface this branch serves NO read route for (round-3 #17). The generic instruction is itself a
   * remedy, and on such a surface it is one the operator cannot carry out.
   */
  conflictNote?: ReactNode;
}): ReactElement {
  const { t } = useI18n();

  if (error instanceof ConfirmationRequired) {
    return (
      <div className="state state--error" role="alert" data-testid="confirmation-required">
        <p className="state__title">{t('guard.typed.mismatch')}</p>
        <p>{t('guard.typed.label', { value: error.expected })}</p>
      </div>
    );
  }

  if (error instanceof ApiError && error.needsStepUp) {
    return <StepUpPanel action={action} onElevated={retryExact} />;
  }

  if (error instanceof ApiError && error.isVersionConflict) {
    return (
      <div className="state state--error" role="alert" data-testid="version-conflict">
        <p className="state__title">{t('conflict.title')}</p>
        <p>{conflictNote ?? t('conflict.body')}</p>
        <p className="state__meta ltr">
          {error.status} · {error.problem.code}
        </p>
        {onReread ? (
          <button type="button" className="btn btn--secondary btn--small" onClick={onReread}>
            {t('conflict.reread')}
          </button>
        ) : null}
      </div>
    );
  }

  /**
   * A 409 THAT IS A DECISION, RENDERED AS ONE (round-2 M8).
   *
   * It is a separate surface from the version conflict above because it is a separate thing: the
   * server is not saying "you were looking at an old copy", it is saying "no — the approval is
   * pending / the seat is taken / this key was used for something else". There is no re-read
   * remedy, and offering one would be a third way of telling the operator something untrue.
   * `idempotency.in_progress` is the single exception: the first attempt is still running, and the
   * SAME intent re-sent under the SAME key is exactly what collects its outcome.
   */
  if (error instanceof ApiError && error.isDecidedConflict) {
    return (
      <div className="state state--error" role="alert" data-testid="decided-conflict">
        <p className="state__title">
          {error.isApprovalPending
            ? t('conflict.approvalPending')
            : error.isIdempotencyInProgress
              ? t('conflict.inProgress')
              : error.isIdempotencyConflict
                ? t('conflict.keyReuse')
                : t('conflict.decided')}
        </p>
        <p>
          <bdi dir="auto">{stripBidiControls(error.problem.detail ?? error.problem.message_key)}</bdi>
        </p>
        <p className="state__meta ltr">
          {error.status} · {error.problem.code}
        </p>
        {error.isIdempotencyInProgress ? (
          <button type="button" className="btn btn--secondary btn--small" onClick={retryExact}>
            {t('conflict.collect')}
          </button>
        ) : null}
      </div>
    );
  }

  /**
   * THE EXACT REPLAY, FOR EVERY OUTCOME THIS CLIENT CANNOT NAME (round-5 A4).
   *
   * This was `isTransportFailure` alone, so a 500, a 502 from a proxy or a 504 on a slow commit fell
   * through to the plain `ErrorState` below — drawn as a decided refusal, with the intent no longer
   * reachable from the screen. The recovery on offer was therefore a FRESH execution somewhere else
   * on the page, under a new key, which is exactly what must not happen while an earlier attempt may
   * have committed. `outcomeUnknown` names the whole set; the remedy is the same for all of it,
   * because the frozen key is the only thing that can settle it.
   */
  if (error instanceof ApiError && error.outcomeUnknown) {
    return <ErrorState error={error} onRetry={retryExact} />;
  }

  return <ErrorState error={error} />;
}
